import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { usePageVisibilityPolling } from '../hooks/usePageVisibilityPolling';
import {
  ApiError,
  type OperatorProfile,
  type StoaJob,
  type ConsoleStall,
  type ConsoleBalance,
  type ConsoleAgent,
  type StoaReceipt,
  type StoaJobComment,
  type PlatformHealth,
  type HungJob,
  type AgentSubkey,
  type DutySwitch,
} from '../api/client';
import StallPicker, {
  type StallPickerViewMode,
  type StallPickerSortMode,
} from '../components/StallPicker';
import TenantOnboardingFlow from '../components/TenantOnboardingFlow';
import StripeConnectCard from '../components/StripeConnectCard';
import ResendVerification from '../components/ResendVerification';

const STALL_PICKER_PREF_KEY = (operatorId: string) =>
  `aexgora-console:stall-picker:${operatorId}`;

interface StallPickerPrefs {
  viewMode: StallPickerViewMode;
  sortMode: StallPickerSortMode;
}

const DEFAULT_STALL_PICKER_PREFS: StallPickerPrefs = {
  viewMode: 'cards',
  sortMode: 'name',
};

function isViewMode(v: unknown): v is StallPickerViewMode {
  return v === 'cards' || v === 'checkboxes' || v === 'dropdown';
}
function isSortMode(v: unknown): v is StallPickerSortMode {
  return v === 'name' || v === 'activity' || v === 'status';
}

type Tab = 'jobs' | 'stalls' | 'agents' | 'balance' | 'receipts' | 'ops';

const STATUS_FILTERS = [
  'all',
  'posted',
  'hired',
  'in_progress',
  'delivered',
  'accepted',
  'settled',
  'rejected',
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number];

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString();
}

// Defensive: API responses occasionally arrive with decimal strings instead
// of numbers (Postgres NUMERIC default behavior). The server-side fix lives
// in knexfile.ts; this guard makes sure a bad value can't blank the whole
// page if the server-side fix is bypassed or regresses.
function formatCurrency(amount: number | string | null | undefined): string {
  const n = typeof amount === 'number' ? amount : Number(amount ?? 0);
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

const ConsolePlaceholder: React.FC = () => {
  const { api, logout, operatorId } = useOperatorAuth();
  const navigate = useNavigate();

  // Profile
  const [profile, setProfile] = useState<OperatorProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<Tab>('jobs');

  // Jobs
  const [jobs, setJobs] = useState<StoaJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Stalls
  const [stalls, setStalls] = useState<ConsoleStall[]>([]);
  const [stallsLoading, setStallsLoading] = useState(false);
  const [stallsError, setStallsError] = useState<string | null>(null);
  // "View Archived" toggle for the Stalls tab (Alex 2026-05-25). When on,
  // we pass include_archived=1 so soft-deleted stalls are surfaced with
  // a Restore button. Off by default — the picker stays clean.
  const [showArchivedStalls, setShowArchivedStalls] = useState(false);
  // Inline two-step confirm state for the archive button. Holds the
  // stall_id whose archive button is in its second-step ("Sure?") state,
  // or null when no confirm is open. Click outside / on another stall's
  // archive button moves the prompt; explicit cancel clears it.
  const [archiveConfirmStallId, setArchiveConfirmStallId] = useState<string | null>(null);
  // Inline funding flow state. fundingStallId holds the stall whose Fund
  // input is currently revealed; fundAmount is the controlled input value.
  // Click Cancel or fund another stall clears it.
  const [fundingStallId, setFundingStallId] = useState<string | null>(null);
  const [fundAmount, setFundAmount] = useState<string>('');
  // Per-row action lock + error surface so the Stalls tab mirrors the
  // Jobs tab's interaction shape.
  const [actingStallId, setActingStallId] = useState<string | null>(null);
  const [stallActionError, setStallActionError] = useState<string | null>(null);
  // Tenant onboarding flow (PR #115 spec) — a Stalls-tab action, not a
  // sixth tab. onboardingResume carries the half-finished stall when the
  // operator re-enters from a row; null means a fresh flow from step 1.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingResume, setOnboardingResume] = useState<ConsoleStall | null>(null);

  // Balance
  const [balance, setBalance] = useState<ConsoleBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  // Receipts
  const [receipts, setReceipts] = useState<StoaReceipt[]>([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState<string | null>(null);

  // Per-agent (persona) filter for Jobs tab. Modes:
  //   'all'        → operator-view, every job (default)
  //   'unassigned' → operator-view, jobs with no assigned_agent_id
  //   <agent_id>   → AGENT-PERSPECTIVE mode (Ponder 2026-05-20 Synedrion
  //                  dispatch surface). Renders inbox + outbox for that
  //                  agent and lets the operator post-as-agent. The
  //                  selected agent_id must be in `agents` (owned by the
  //                  authenticated operator); the server-side ownership
  //                  gate enforces this independently.
  const [agents, setAgents] = useState<ConsoleAgent[]>([]);

  // ---- Agents tab (R-V1/R-V2, job 42dc2eef) -------------------------------
  // The console's first agent-inventory surface: every agent this operator
  // owns, with bulk retire/restore and the K-V1 listing preference.
  //
  // LIFECYCLE HONESTY. GET /stoa/console/agents does not report retired_at /
  // is_listed yet (contract ask filed — Keel's lane). Until it does, the only
  // authoritative state this client holds is what action responses said THIS
  // SESSION. `agentLifecycle` records exactly that; everything else renders
  // as "not reported", never as a guessed badge (The Console Does Not Lie).
  const [agentLifecycle, setAgentLifecycle] = useState<
    Record<string, { retired_at?: string | null; is_listed?: boolean }>
  >({});
  const [agentSelection, setAgentSelection] = useState<Set<string>>(new Set());
  const [agentFilterText, setAgentFilterText] = useState('');
  // Bulk run state: which verb is confirming / running, plus per-row outcomes
  // from the last run ("retired", "409: archive stall X first", …).
  const [bulkConfirmVerb, setBulkConfirmVerb] = useState<
    null | 'retire' | 'restore' | 'list' | 'unlist'
  >(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [agentActionResults, setAgentActionResults] = useState<
    Record<string, { ok: boolean; note: string }>
  >({});

  // ---- Ops tab ------------------------------------------------------------
  // Platform controls that had API routes but no surface: the money
  // kill-switch, the hung-job buckets, and per-surface subkeys (migration 085).
  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [hung, setHung] = useState<{ blocked: HungJob[]; dead: HungJob[] } | null>(null);
  // Set/clear the halt. Two-step confirm on both directions: clearing lets
  // money move again, and setting it stops settlement mid-flight.
  const [haltBusy, setHaltBusy] = useState(false);
  const [haltConfirm, setHaltConfirm] = useState<null | 'set' | 'clear'>(null);
  const [haltReason, setHaltReason] = useState('');
  const [haltNotice, setHaltNotice] = useState<string | null>(null);

  // Subkeys: which agent's surfaces we're looking at, its keys, and the
  // one-time private key from the most recent mint.
  const [subkeyAgentId, setSubkeyAgentId] = useState<string>('');
  const [subkeys, setSubkeys] = useState<AgentSubkey[]>([]);
  const [subkeysLoading, setSubkeysLoading] = useState(false);
  const [subkeyError, setSubkeyError] = useState<string | null>(null);
  const [newSubkeyLabel, setNewSubkeyLabel] = useState('');
  // The mint response is the ONLY time the private key exists outside the
  // caller's hands. Held in memory, never persisted, cleared on dismiss.
  const [mintedKey, setMintedKey] = useState<{ label: string; private_key: string } | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);

  // Duty switches (087). Board-side seat on/off; the Box syncs these down to
  // its switch files, so a flip here takes effect on its next tick, not
  // instantly. The UI says so rather than implying it is live.
  const [dutySwitches, setDutySwitches] = useState<DutySwitch[]>([]);
  const [switchBusyKey, setSwitchBusyKey] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [personaFilter, setPersonaFilter] = useState<string>('all');

  // Agent-perspective inbox + outbox state (only populated when
  // personaFilter is an agent_id).
  const [inboxJobs, setInboxJobs] = useState<StoaJob[]>([]);
  const [outboxJobs, setOutboxJobs] = useState<StoaJob[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [outboxError, setOutboxError] = useState<string | null>(null);

  // Agent-perspective "+ New Job" form. parent_job_id is required for
  // /stoa/jobs/by-agent and chosen from the agent's inbox.
  const [agentCreateOpen, setAgentCreateOpen] = useState(false);
  const [agentCreateTitle, setAgentCreateTitle] = useState('');
  const [agentCreateDescription, setAgentCreateDescription] = useState('');
  const [agentCreateStallId, setAgentCreateStallId] = useState('');
  const [agentCreateRate, setAgentCreateRate] = useState('');
  const [agentCreateParentJobId, setAgentCreateParentJobId] = useState('');
  const [agentCreateSubmitting, setAgentCreateSubmitting] = useState(false);
  const [agentCreateError, setAgentCreateError] = useState<string | null>(null);

  // In-flight per-row action lock (so the user can't double-click a transition)
  const [actingJobId, setActingJobId] = useState<string | null>(null);

  // Inline deliver-form state (when user clicks Deliver on an in_progress row,
  // we expand a small form below that row)
  const [deliverFormJobId, setDeliverFormJobId] = useState<string | null>(null);
  const [deliverNote, setDeliverNote] = useState('');
  const [deliverLink, setDeliverLink] = useState('');

  // Click-to-expand detail for a job row. Single-row open at a time so the
  // list stays scannable.
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandedStallId, setExpandedStallId] = useState<string | null>(null);

  // Stall picker layout preference (Alex 2026-05-25 — dropdown gets clumsy as
  // the roster grows). Per-operator, localStorage-backed; hydrated once when
  // operatorId resolves so we don't clobber saved prefs with the default.
  const [stallPickerPrefs, setStallPickerPrefs] = useState<StallPickerPrefs>(
    DEFAULT_STALL_PICKER_PREFS,
  );
  const stallPickerPrefsHydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!operatorId || stallPickerPrefsHydratedFor.current === operatorId) return;
    try {
      const raw = window.localStorage.getItem(STALL_PICKER_PREF_KEY(operatorId));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<StallPickerPrefs>;
        setStallPickerPrefs({
          viewMode: isViewMode(parsed.viewMode)
            ? parsed.viewMode
            : DEFAULT_STALL_PICKER_PREFS.viewMode,
          sortMode: isSortMode(parsed.sortMode)
            ? parsed.sortMode
            : DEFAULT_STALL_PICKER_PREFS.sortMode,
        });
      }
    } catch {
      // localStorage may be disabled / quota exceeded — defaults are fine.
    }
    stallPickerPrefsHydratedFor.current = operatorId;
  }, [operatorId]);
  useEffect(() => {
    if (!operatorId || stallPickerPrefsHydratedFor.current !== operatorId) return;
    try {
      window.localStorage.setItem(
        STALL_PICKER_PREF_KEY(operatorId),
        JSON.stringify(stallPickerPrefs),
      );
    } catch {
      // ignore — pref persistence is best-effort.
    }
  }, [operatorId, stallPickerPrefs]);
  const setStallPickerViewMode = useCallback(
    (m: StallPickerViewMode) => setStallPickerPrefs((p) => ({ ...p, viewMode: m })),
    [],
  );
  const setStallPickerSortMode = useCallback(
    (s: StallPickerSortMode) => setStallPickerPrefs((p) => ({ ...p, sortMode: s })),
    [],
  );

  // Chat-protocol layer (Founder 2026-05-20). Comments on the currently-
  // expanded job. Polled every 2s while a card is expanded; cleared on
  // collapse. Only one job is ever expanded at a time so the state is
  // single-bucket.
  const [comments, setComments] = useState<StoaJobComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyPosting, setReplyPosting] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  // Job creation form
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createStallId, setCreateStallId] = useState('');
  const [createAgentId, setCreateAgentId] = useState('');
  const [createRate, setCreateRate] = useState('');
  const [createPrivate, setCreatePrivate] = useState(true);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Refresh the operator profile (re-reads balance after mutations
  // that move $AEX in or out of the operator wallet).
  const refreshProfile = useCallback(() => {
    api
      .getOperatorProfile()
      .then((p) => setProfile(p))
      .catch(() => {
        /* swallow — top-card stale by one mutation is benign */
      });
  }, [api]);

  // Fetch profile on mount
  useEffect(() => {
    let cancelled = false;
    api
      .getOperatorProfile()
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) setProfileError(err.message);
        else setProfileError('Failed to load profile.');
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Fetch agents (personas) for the per-agent filter dropdown. Re-fetchable
  // because tenant onboarding can register a new agent mid-session and the
  // flow's "use an existing agent" resume path reads this list.
  const fetchAgents = useCallback(() => {
    api
      .getConsoleAgents()
      .then((r) => setAgents(r.agents ?? []))
      .catch(() => {
        /* silent — persona filter just degrades to 'All' if this fails */
      });
  }, [api]);
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Bulk lifecycle runner (R-V1). Sequential on purpose: 110 parallel
  // POSTs is a rate-limiter trip and an unreadable failure mode; one at a
  // time gives a progress count and a per-row outcome the operator can act
  // on ("409 — archive Smoke Stall X first"). Each success records the
  // authoritative state the server returned; only successes leave the
  // selection, so a re-click retries exactly the failures.
  const runBulkAgentAction = useCallback(
    async (verb: 'retire' | 'restore' | 'list' | 'unlist') => {
      const ids = Array.from(agentSelection);
      if (ids.length === 0) return;
      setBulkRunning(true);
      setBulkConfirmVerb(null);
      setBulkProgress({ done: 0, total: ids.length });
      const results: Record<string, { ok: boolean; note: string }> = {};
      const lifecycle: Record<string, { retired_at?: string | null; is_listed?: boolean }> = {};
      const succeeded: string[] = [];
      for (const id of ids) {
        try {
          if (verb === 'retire') {
            const r = await api.retireAgent(id);
            lifecycle[id] = { retired_at: r.retired_at };
            results[id] = { ok: true, note: 'Retired' };
          } else if (verb === 'restore') {
            await api.restoreAgent(id);
            lifecycle[id] = { retired_at: null };
            results[id] = { ok: true, note: 'Restored' };
          } else {
            const r = await api.setAgentListing(id, verb === 'list');
            lifecycle[id] = { is_listed: r.is_listed };
            results[id] = { ok: true, note: r.is_listed ? 'Listed' : 'Unlisted' };
          }
          succeeded.push(id);
        } catch (err: unknown) {
          if (err instanceof ApiError) {
            const stalls = (err.body as { live_stalls?: { stall_id: string; name: string }[] } | null)
              ?.live_stalls;
            results[id] = {
              ok: false,
              note:
                err.status === 409 && stalls && stalls.length > 0
                  ? `Works live stall${stalls.length === 1 ? '' : 's'} ${stalls
                      .map((s) => `“${s.name}”`)
                      .join(', ')} — archive the stall first, then retire.`
                  : `${err.status}: ${err.message}`,
            };
          } else {
            results[id] = { ok: false, note: 'Network error — state unknown, re-check before retrying.' };
          }
        }
        setBulkProgress((p) => (p ? { done: p.done + 1, total: p.total } : p));
      }
      setAgentActionResults((prev) => ({ ...prev, ...results }));
      setAgentLifecycle((prev) => {
        const next = { ...prev };
        for (const [id, patch] of Object.entries(lifecycle)) {
          next[id] = { ...next[id], ...patch };
        }
        return next;
      });
      setAgentSelection((prev) => {
        const next = new Set(prev);
        for (const id of succeeded) next.delete(id);
        return next;
      });
      setBulkRunning(false);
      setBulkProgress(null);
      fetchAgents();
    },
    [api, agentSelection, fetchAgents],
  );

  // In-flight cancellation (job id being cancelled, null when none)
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);

  // Fetch jobs
  const fetchJobs = useCallback(() => {
    setJobsLoading(true);
    setJobsError(null);
    const params = statusFilter !== 'all' ? { stoa_status: statusFilter } : undefined;
    api
      .getConsoleJobs(params)
      .then((res) => {
        setJobs(res?.jobs ?? []);
      })
      .catch((err: unknown) => {
        setJobsError(err instanceof ApiError ? err.message : 'Failed to load jobs.');
      })
      .finally(() => setJobsLoading(false));
  }, [api, statusFilter]);

  // Cancel a posted/hired job. Refunds escrow to the operator balance.
  // window.confirm is intentionally low-fi; richer modal can land later
  // if the operator interaction warrants it.
  const cancelJob = useCallback(
    (job: StoaJob) => {
      const ok = window.confirm(
        `Cancel "${job.title}"? This refunds ${formatCurrency(job.offered_rate)} ${job.rate_currency} to your balance.`,
      );
      if (!ok) return;
      setCancellingJobId(job.id);
      setJobsError(null);
      api
        .cancelJob(job.id)
        .then(() => {
          fetchJobs();
          // Cancel refunds escrow → operator balance changes. Refresh the
          // top card so the displayed balance matches reality.
          refreshProfile();
        })
        .catch((err: unknown) => {
          setJobsError(err instanceof ApiError ? err.message : 'Failed to cancel job.');
        })
        .finally(() => setCancellingJobId(null));
    },
    [api, fetchJobs, refreshProfile],
  );

  // Agent-perspective inbox/outbox fetchers (Ponder 2026-05-20).
  // Declared before runTransition because that callback's dependency
  // array closes over them — putting them after triggers a TDZ error.
  const fetchAgentInbox = useCallback(
    (agentId: string) => {
      setInboxLoading(true);
      setInboxError(null);
      api
        .getAgentInbox(agentId)
        .then((res) => setInboxJobs(res?.jobs ?? []))
        .catch((err: unknown) => {
          setInboxError(err instanceof ApiError ? err.message : 'Failed to load inbox.');
        })
        .finally(() => setInboxLoading(false));
    },
    [api],
  );

  const fetchAgentOutbox = useCallback(
    (agentId: string) => {
      setOutboxLoading(true);
      setOutboxError(null);
      api
        .getAgentOutbox(agentId)
        .then((res) => setOutboxJobs(res?.jobs ?? []))
        .catch((err: unknown) => {
          setOutboxError(err instanceof ApiError ? err.message : 'Failed to load outbox.');
        })
        .finally(() => setOutboxLoading(false));
    },
    [api],
  );

  // Re-fetch inbox/outbox when the operator switches into agent-perspective
  // mode. The operator modes ('all'/'unassigned') leave this state alone.
  useEffect(() => {
    if (personaFilter === 'all' || personaFilter === 'unassigned') return;
    if (activeTab !== 'jobs') return;
    fetchAgentInbox(personaFilter);
    fetchAgentOutbox(personaFilter);
  }, [personaFilter, activeTab, fetchAgentInbox, fetchAgentOutbox]);

  // Comment thread fetcher + 2s polling while a job card is expanded
  // (chat-protocol layer). Cleared on collapse or job change so the
  // reply box starts blank for each new card.
  const fetchComments = useCallback(
    (jobId: string) => {
      api
        .getJobComments(jobId)
        .then((res) => {
          setComments(res?.comments ?? []);
          setCommentsError(null);
        })
        .catch((err: unknown) => {
          setCommentsError(err instanceof ApiError ? err.message : 'Failed to load comments.');
        })
        .finally(() => setCommentsLoading(false));
    },
    [api],
  );

  // Reset comment state when job card is collapsed/changed
  useEffect(() => {
    if (!expandedJobId) {
      setComments([]);
      setCommentsError(null);
      setReplyBody('');
      setReplyError(null);
      return;
    }
    // Initial fetch when job card is expanded
    setCommentsLoading(true);
    fetchComments(expandedJobId);
  }, [expandedJobId, fetchComments]);

  // Auto-refresh comments every 2 seconds while job card is expanded and page is visible
  usePageVisibilityPolling({
    onPoll: () => {
      if (expandedJobId && !commentsLoading) {
        fetchComments(expandedJobId);
      }
    },
    intervalMs: 2_000, // 2 seconds (same as before)
    enabled: !!expandedJobId,
  });

  // Submit a reply. Author depends on mode:
  //   • agent-perspective (personaFilter is an agent_id) → the persona
  //     being viewed-as is the author
  //   • operator mode → the operator's umbrella agent (first agent owned
  //     by the operator) is the fallback author. Adequate for now;
  //     refine when operators routinely commission jobs under several
  //     own-agent identities and need to pick at comment time.
  const submitReply = useCallback(() => {
    if (!expandedJobId) return;
    const body = replyBody.trim();
    if (!body) {
      setReplyError('Comment cannot be empty.');
      return;
    }
    const isAgentPerspective = personaFilter !== 'all' && personaFilter !== 'unassigned';
    const authorAgentId = isAgentPerspective ? personaFilter : agents[0]?.agent_id;
    if (!authorAgentId) {
      setReplyError('No agent available to author this comment. Register or load agents first.');
      return;
    }
    setReplyPosting(true);
    setReplyError(null);
    api
      .postJobCommentAsOperator(expandedJobId, { body, author_agent_id: authorAgentId })
      .then(() => {
        setReplyBody('');
        // Optimistic refresh so the new comment shows immediately.
        fetchComments(expandedJobId);
      })
      .catch((err: unknown) => {
        setReplyError(err instanceof ApiError ? err.message : 'Failed to post comment.');
      })
      .finally(() => setReplyPosting(false));
  }, [api, expandedJobId, replyBody, personaFilter, agents, fetchComments]);

  // Generic transition runner — applies an api call to a job, manages the
  // global action lock, and refreshes jobs + profile (escrow movement) on
  // success. Surfaces errors via setJobsError. In agent-perspective mode
  // also re-fetches inbox/outbox so the row reflects its new state.
  const runTransition = useCallback(
    async (jobId: string, fn: () => Promise<unknown>) => {
      setActingJobId(jobId);
      setJobsError(null);
      try {
        await fn();
        if (personaFilter === 'all' || personaFilter === 'unassigned') {
          fetchJobs();
        } else {
          fetchAgentInbox(personaFilter);
          fetchAgentOutbox(personaFilter);
        }
        refreshProfile();
      } catch (err) {
        setJobsError(err instanceof ApiError ? err.message : 'Action failed.');
      } finally {
        setActingJobId(null);
      }
    },
    [personaFilter, fetchJobs, fetchAgentInbox, fetchAgentOutbox, refreshProfile],
  );

  const claimJob = (job: StoaJob) => {
    // In agent-perspective mode the operator is acting AS the viewed
    // agent — claim assigns the job to that agent regardless of any
    // existing assigned_agent_id. In operator mode we require an existing
    // assignment because there's no implied claimant.
    const isAgentPerspective = personaFilter !== 'all' && personaFilter !== 'unassigned';
    const targetAgentId = isAgentPerspective ? personaFilter : job.assigned_agent_id;
    if (!targetAgentId) {
      setJobsError('Job has no assigned agent — claim from an unassigned job is not supported yet.');
      return;
    }
    void runTransition(job.id, () => api.hireJob(job.id, targetAgentId));
  };

  const startJob = (job: StoaJob) => {
    void runTransition(job.id, () => api.startJob(job.id));
  };

  const acceptJob = (job: StoaJob) => {
    void runTransition(job.id, () => api.acceptJob(job.id));
  };

  const rejectJob = (job: StoaJob) => {
    const reason = window.prompt(`Reject "${job.title}"? Enter rejection reason:`);
    if (!reason || !reason.trim()) return;
    void runTransition(job.id, () => api.rejectJob(job.id, reason.trim()));
  };

  const settleJob = (job: StoaJob) => {
    const ok = window.confirm(
      `Settle "${job.title}"? This finalizes payout (${formatCurrency(job.offered_rate)} ${job.rate_currency} → stall wallet, minus platform fee) and generates a signed receipt.`,
    );
    if (!ok) return;
    void runTransition(job.id, () => api.settleJob(job.id));
  };

  const openDeliverForm = (job: StoaJob) => {
    setDeliverFormJobId(job.id);
    setDeliverNote('');
    setDeliverLink('');
  };

  const submitDeliver = (job: StoaJob) => {
    const note = deliverNote.trim();
    void runTransition(job.id, () =>
      api.deliverAsAgent(job.id, {
        delivery_note: note || undefined,
        delivery_link: deliverLink.trim() || undefined,
      }).then(() => {
        setDeliverFormJobId(null);
        setDeliverNote('');
        setDeliverLink('');
      }),
    );
  };

  // Submit the Job creation form. Validates inputs, calls createJob,
  // resets/closes form on success, refreshes the jobs list + profile
  // (escrow leaves the operator wallet on create).
  const submitJobCreate = useCallback(() => {
    const title = createTitle.trim();
    const description = createDescription.trim();
    const rate = Number(createRate);
    if (!title || !description || !createStallId || !Number.isFinite(rate) || rate <= 0) {
      setCreateError('Title, description, stall, and a positive rate are all required.');
      return;
    }
    setCreateSubmitting(true);
    setCreateError(null);
    api
      .createJob({
        stall_id: createStallId,
        title,
        description,
        offered_rate: rate,
        is_private: createPrivate,
        ...(createAgentId ? { assigned_agent_id: createAgentId } : {}),
      })
      .then(() => {
        // Reset form
        setCreateTitle('');
        setCreateDescription('');
        setCreateStallId('');
        setCreateAgentId('');
        setCreateRate('');
        setCreatePrivate(true);
        setCreateOpen(false);
        // Surface the new job in the list + reflect the escrow debit.
        fetchJobs();
        refreshProfile();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setCreateError(err.message);
        } else {
          setCreateError('Failed to create job.');
        }
      })
      .finally(() => setCreateSubmitting(false));
  }, [api, createTitle, createDescription, createStallId, createAgentId, createRate, createPrivate, fetchJobs, refreshProfile]);

  // Agent-perspective inbox/outbox fetchers (Ponder 2026-05-20).
  // Triggered when personaFilter is set to an agent_id.
  // Declared BEFORE runTransition because runTransition's dependency array
  // closes over them — putting them after triggers a temporal-dead-zone
  // ReferenceError on first render.

  // Submit the agent-perspective new-job form. Calls
  // postJobByAgentAsOperator (operator-side proxy for /by-agent) — the
  // Console uses the operator Bearer token to post on behalf of an owned
  // agent. Escrow still draws from that agent's stall wallet, not the
  // operator wallet. parent_job_id is required.
  const submitAgentJobCreate = useCallback(() => {
    if (personaFilter === 'all' || personaFilter === 'unassigned') return;
    const title = agentCreateTitle.trim();
    const description = agentCreateDescription.trim();
    const rate = Number(agentCreateRate);
    if (!title || !description || !agentCreateStallId || !agentCreateParentJobId || !Number.isFinite(rate) || rate <= 0) {
      setAgentCreateError('Title, description, target stall, parent job, and positive rate are required.');
      return;
    }
    setAgentCreateSubmitting(true);
    setAgentCreateError(null);
    api
      .postJobByAgentAsOperator({
        stall_id: agentCreateStallId,
        title,
        description,
        offered_rate: rate,
        parent_job_id: agentCreateParentJobId,
        posted_by_agent_id: personaFilter,
        is_private: true,
      })
      .then(() => {
        setAgentCreateTitle('');
        setAgentCreateDescription('');
        setAgentCreateStallId('');
        setAgentCreateRate('');
        setAgentCreateParentJobId('');
        setAgentCreateOpen(false);
        // Refresh the outbox — the new job appears here as a sent message.
        fetchAgentOutbox(personaFilter);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) setAgentCreateError(err.message);
        else setAgentCreateError('Failed to post job.');
      })
      .finally(() => setAgentCreateSubmitting(false));
  }, [api, personaFilter, agentCreateTitle, agentCreateDescription, agentCreateStallId, agentCreateRate, agentCreateParentJobId, fetchAgentOutbox]);

  // Fetch stalls. Passes include_archived when the toggle is on so
  // archived rows arrive in the same list and render with a Restore
  // button instead of Archive.
  const fetchStalls = useCallback(() => {
    setStallsLoading(true);
    setStallsError(null);
    api
      .getConsoleStalls(showArchivedStalls ? { includeArchived: true } : undefined)
      .then((res) => {
        setStalls(res?.stalls ?? []);
      })
      .catch((err: unknown) => {
        setStallsError(err instanceof ApiError ? err.message : 'Failed to load stalls.');
      })
      .finally(() => setStallsLoading(false));
  }, [api, showArchivedStalls]);

  // Archive (soft-delete) a stall. Two-step confirm flow:
  //   1st click → opens the inline "Sure?" prompt for this stall_id
  //   2nd click (yes) → calls DELETE /stoa/stalls/:id and refetches
  // The confirm is handled by the renderer; this function performs the
  // actual mutation once the operator has confirmed.
  const archiveStall = useCallback(
    (stall: ConsoleStall) => {
      setActingStallId(stall.stall_id);
      setStallActionError(null);
      api
        .archiveStall(stall.stall_id)
        .then(() => {
          setArchiveConfirmStallId(null);
          // Refetch so the row drops out (or stays with archived state
          // surfaced if the View Archived toggle is on).
          fetchStalls();
        })
        .catch((err: unknown) => {
          setStallActionError(
            err instanceof ApiError
              ? `Failed to archive "${stall.name}": ${err.message}`
              : `Failed to archive "${stall.name}".`,
          );
        })
        .finally(() => setActingStallId(null));
    },
    [api, fetchStalls],
  );

  // Operator grants operating budget — transfers AEX from operator balance
  // to the stall's wallet (budget-grant mneme 2026-05-26). Validates the
  // amount before firing; the server enforces ownership + positivity again.
  // On success, refetch stalls so the wallet badge updates in place.
  const fundStall = useCallback(
    (stall: ConsoleStall, amount: number) => {
      setActingStallId(stall.stall_id);
      setStallActionError(null);
      api
        .fundStall(stall.stall_id, amount)
        .then(() => {
          setFundingStallId(null);
          setFundAmount('');
          fetchStalls();
        })
        .catch((err: unknown) => {
          setStallActionError(
            err instanceof ApiError
              ? `Failed to fund "${stall.name}": ${err.message}`
              : `Failed to fund "${stall.name}".`,
          );
        })
        .finally(() => setActingStallId(null));
    },
    [api, fetchStalls],
  );

  // Restore (un-archive) a stall. Single-step — restore is recoverable and
  // doesn't warrant the same friction as archive. Clears archived_at; the
  // operator still needs to explicitly go-live again before the stall is
  // pickable for jobs (server preserves is_live=false from the archive).
  const restoreStall = useCallback(
    (stall: ConsoleStall) => {
      setActingStallId(stall.stall_id);
      setStallActionError(null);
      api
        .restoreStall(stall.stall_id)
        .then(() => fetchStalls())
        .catch((err: unknown) => {
          setStallActionError(
            err instanceof ApiError
              ? `Failed to restore "${stall.name}": ${err.message}`
              : `Failed to restore "${stall.name}".`,
          );
        })
        .finally(() => setActingStallId(null));
    },
    [api, fetchStalls],
  );

  // Fetch balance
  const fetchBalance = useCallback(() => {
    setBalanceLoading(true);
    setBalanceError(null);
    api
      .getConsoleBalance()
      .then((res) => {
        setBalance(res);
      })
      .catch((err: unknown) => {
        setBalanceError(err instanceof ApiError ? err.message : 'Failed to load balance.');
      })
      .finally(() => setBalanceLoading(false));
  }, [api]);

  // Ops: platform health (halt state + spend windows) and the hung buckets.
  // /health is unauthenticated; hung-jobs is operator-scoped. A failure on
  // either must not blank the other, so they settle independently.
  const fetchOps = useCallback(() => {
    setOpsLoading(true);
    setOpsError(null);
    Promise.allSettled([api.getHealth(), api.getHungJobs(), api.getDutySwitches()])
      .then(([healthRes, hungRes, switchRes]) => {
        if (healthRes.status === 'fulfilled') setHealth(healthRes.value);
        if (hungRes.status === 'fulfilled') {
          setHung({ blocked: hungRes.value.blocked, dead: hungRes.value.dead });
        }
        if (switchRes.status === 'fulfilled') setDutySwitches(switchRes.value.switches);
        const failed = [healthRes, hungRes, switchRes].filter((r) => r.status === 'rejected');
        if (failed.length) {
          const first = (failed[0] as PromiseRejectedResult).reason;
          setOpsError(first instanceof ApiError ? first.message : 'Some ops data failed to load.');
        }
      })
      .finally(() => setOpsLoading(false));
  }, [api]);

  const fetchSubkeys = useCallback((agentId: string) => {
    if (!agentId) { setSubkeys([]); return; }
    setSubkeysLoading(true);
    setSubkeyError(null);
    api
      .listSubkeys(agentId)
      .then((res) => setSubkeys(res.subkeys))
      .catch((err: unknown) => {
        setSubkeyError(err instanceof ApiError ? err.message : 'Failed to load subkeys.');
      })
      .finally(() => setSubkeysLoading(false));
  }, [api]);

  const applyHalt = useCallback((halted: boolean) => {
    setHaltBusy(true);
    setHaltNotice(null);
    api
      .setSpendHalt({ halted, reason: haltReason.trim() || undefined })
      .then((state) => {
        setHealth((h) => (h ? { ...h, spend: { ...h.spend, ...state } } : h));
        setHaltNotice(halted ? 'Money operations HALTED.' : 'Halt cleared — money operations resume.');
        setHaltConfirm(null);
        setHaltReason('');
        fetchOps();
      })
      .catch((err: unknown) => {
        // 403 here is the common one: the halt is AEXTech-admin only, while
        // every other console call works for any operator. Say so plainly.
        setHaltNotice(
          err instanceof ApiError && err.status === 403
            ? 'Refused: the spend halt is AEXTech-admin only (adminEmails allowlist).'
            : err instanceof ApiError ? err.message : 'Halt change failed.'
        );
      })
      .finally(() => setHaltBusy(false));
  }, [api, haltReason, fetchOps]);

  const toggleDutySwitch = useCallback((sw: DutySwitch) => {
    setSwitchBusyKey(sw.switch_key);
    setSwitchError(null);
    api
      .setDutySwitch(sw.switch_key, { switched_on: !sw.switched_on, kind: sw.kind })
      .then((res) => {
        setDutySwitches((rows) => rows.map((r) => (r.switch_key === res.switch.switch_key ? res.switch : r)));
      })
      .catch((err: unknown) => {
        setSwitchError(
          err instanceof ApiError && err.status === 403
            ? 'Refused: setting a switch is AEXTech-admin only.'
            : err instanceof ApiError ? err.message : 'Switch change failed.'
        );
      })
      .finally(() => setSwitchBusyKey(null));
  }, [api]);

  const doMintSubkey = useCallback(() => {
    const label = newSubkeyLabel.trim();
    if (!subkeyAgentId || !label) return;
    setSubkeysLoading(true);
    setSubkeyError(null);
    api
      .mintSubkey(subkeyAgentId, { label })
      .then((res) => {
        setMintedKey({ label: res.subkey.label, private_key: res.private_key });
        setNewSubkeyLabel('');
        fetchSubkeys(subkeyAgentId);
      })
      .catch((err: unknown) => {
        setSubkeyError(err instanceof ApiError ? err.message : 'Mint failed.');
      })
      .finally(() => setSubkeysLoading(false));
  }, [api, subkeyAgentId, newSubkeyLabel, fetchSubkeys]);

  const doRevokeSubkey = useCallback((subkeyId: string) => {
    if (!subkeyAgentId) return;
    setSubkeysLoading(true);
    api
      .revokeSubkey(subkeyAgentId, subkeyId, 'revoked from console')
      .then(() => {
        setRevokeConfirmId(null);
        fetchSubkeys(subkeyAgentId);
      })
      .catch((err: unknown) => {
        setSubkeyError(err instanceof ApiError ? err.message : 'Revoke failed.');
      })
      .finally(() => setSubkeysLoading(false));
  }, [api, subkeyAgentId, fetchSubkeys]);

  // Fetch receipts
  const fetchReceipts = useCallback(() => {
    setReceiptsLoading(true);
    setReceiptsError(null);
    api
      .getConsoleReceipts()
      .then((res) => {
        setReceipts(res?.receipts ?? []);
      })
      .catch((err: unknown) => {
        setReceiptsError(err instanceof ApiError ? err.message : 'Failed to load receipts.');
      })
      .finally(() => setReceiptsLoading(false));
  }, [api]);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'jobs') fetchJobs();
    else if (activeTab === 'stalls') fetchStalls();
    else if (activeTab === 'agents') fetchAgents();
    else if (activeTab === 'balance') fetchBalance();
    else if (activeTab === 'receipts') fetchReceipts();
    else if (activeTab === 'ops') fetchOps();
  }, [activeTab, fetchJobs, fetchStalls, fetchAgents, fetchBalance, fetchReceipts, fetchOps]);

  // Load an agent's surfaces when the Ops picker changes.
  useEffect(() => {
    if (activeTab === 'ops') fetchSubkeys(subkeyAgentId);
  }, [activeTab, subkeyAgentId, fetchSubkeys]);

  // Auto-refresh jobs list every 30 seconds while on jobs tab and page is visible
  usePageVisibilityPolling({
    onPoll: () => {
      // Only refresh if not currently loading to avoid conflicting requests
      if (!jobsLoading) {
        fetchJobs();
      }
    },
    intervalMs: 30_000, // 30 seconds
    enabled: activeTab === 'jobs' && (personaFilter === 'all' || personaFilter === 'unassigned'),
  });

  // Auto-refresh agent inbox/outbox every 30 seconds in agent-perspective mode
  usePageVisibilityPolling({
    onPoll: () => {
      if (!inboxLoading && !outboxLoading && personaFilter !== 'all' && personaFilter !== 'unassigned') {
        fetchAgentInbox(personaFilter);
        fetchAgentOutbox(personaFilter);
      }
    },
    intervalMs: 30_000, // 30 seconds
    enabled: activeTab === 'jobs' && personaFilter !== 'all' && personaFilter !== 'unassigned',
  });

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // --- Tab content renderers ---

  const openCreateForm = () => {
    setCreateOpen(true);
    setCreateError(null);
    // Load stalls if they haven't been pulled yet so the dropdown has options.
    if (stalls.length === 0 && !stallsLoading) fetchStalls();
  };

  const renderJobCreateForm = () => {
    if (!createOpen) {
      return (
        <div className="job-create-bar">
          <button type="button" className="job-create-btn" onClick={openCreateForm}>
            + New Job
          </button>
        </div>
      );
    }
    return (
      <form
        className="job-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          submitJobCreate();
        }}
      >
        <div className="job-create-header">
          <span>New job</span>
          <button
            type="button"
            className="job-create-close"
            onClick={() => setCreateOpen(false)}
            disabled={createSubmitting}
          >
            ×
          </button>
        </div>
        <label className="job-create-field">
          <span>Title</span>
          <input
            type="text"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            maxLength={255}
            disabled={createSubmitting}
            required
          />
        </label>
        <label className="job-create-field">
          <span>Description / acceptance criteria</span>
          <textarea
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            maxLength={50000}
            rows={4}
            disabled={createSubmitting}
            required
          />
        </label>
        <label className="job-create-field">
          <span>Hire</span>
          <StallPicker
            stalls={stalls}
            agents={agents}
            value={createStallId}
            onChange={(next) => {
              setCreateStallId(next);
              // Auto-link the agent to the stall's primary agent_id. Each
              // Synedrion persona is one stall + one agent pair; selecting
              // the stall picks the worker automatically. Money flows to
              // the stall's wallet on settle; worker does the delivery.
              const picked = stalls.find((s) => s.stall_id === next);
              setCreateAgentId(picked?.agent_id ?? '');
            }}
            disabled={createSubmitting}
            loading={stallsLoading}
            required
            placeholder="— pick a stall to hire —"
            viewMode={stallPickerPrefs.viewMode}
            setViewMode={setStallPickerViewMode}
            sortMode={stallPickerPrefs.sortMode}
            setSortMode={setStallPickerSortMode}
          />
          {createStallId && (() => {
            const picked = stalls.find((s) => s.stall_id === createStallId);
            if (!picked) return null;
            const worker = picked.agent_id
              ? agents.find((a) => a.agent_id === picked.agent_id)?.display_name
              : null;
            return (
              <div className="hire-preview">
                {worker
                  ? <>Stall wallet: <strong>{picked.name}</strong> · Worker: <strong>{worker}</strong></>
                  : <>Stall wallet: <strong>{picked.name}</strong> · Worker: <em>unassigned (stall hires internally)</em></>}
              </div>
            );
          })()}
        </label>
        <label className="job-create-field">
          <span>Escrow ($AEX)</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={createRate}
            onChange={(e) => setCreateRate(e.target.value)}
            disabled={createSubmitting}
            required
          />
        </label>
        <label className="job-create-checkbox">
          <input
            type="checkbox"
            checked={createPrivate}
            onChange={(e) => setCreatePrivate(e.target.checked)}
            disabled={createSubmitting}
          />
          <span>Private (internal — hidden from public board, no expiration)</span>
        </label>
        {createError && <div className="auth-error" role="alert">{createError}</div>}
        <div className="job-create-actions">
          <button type="submit" className="job-create-submit" disabled={createSubmitting}>
            {createSubmitting ? 'Creating…' : 'Post job + lock escrow'}
          </button>
          <button
            type="button"
            className="job-create-cancel"
            onClick={() => setCreateOpen(false)}
            disabled={createSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  // Map agent_id → display_name for inline labels on job rows.
  const agentNameById = (id: string | null | undefined): string => {
    if (!id) return '—';
    const hit = agents.find((a) => a.agent_id === id);
    return hit ? hit.display_name : truncateId(id);
  };

  // ---- Agent lifecycle helpers (R-V1/R-V2) --------------------------------
  // Three-valued state, and the difference is load-bearing:
  //   true      — retired (server row said so, or an action response did)
  //   false     — known not retired (same sources)
  //   undefined — NOT REPORTED. The listing endpoint doesn't project
  //               lifecycle columns yet, so most rows sit here. undefined
  //               must never render as "Active" — that would be a guess.
  const retiredState = (a: ConsoleAgent): boolean | undefined => {
    if (a.retired_at !== undefined) return a.retired_at !== null;
    const known = agentLifecycle[a.agent_id];
    if (known && known.retired_at !== undefined) return known.retired_at !== null;
    return undefined;
  };
  const listedState = (a: ConsoleAgent): boolean | undefined => {
    if (a.is_listed !== undefined) return a.is_listed;
    return agentLifecycle[a.agent_id]?.is_listed;
  };

  // Duplicate identities (R-V2): repeated mint/registration runs left e.g.
  // six Emporion rows under one operator. Which row is canonical is a DATA
  // question for the cleanup, not a UI question — the console's job is to
  // make the duplicates visible and distinguishable, not to pick a winner.
  const duplicateNameCounts = new Map<string, number>();
  for (const a of agents) {
    const key = a.display_name.trim().toLowerCase();
    duplicateNameCounts.set(key, (duplicateNameCounts.get(key) ?? 0) + 1);
  }
  const isDuplicateName = (a: ConsoleAgent): boolean =>
    (duplicateNameCounts.get(a.display_name.trim().toLowerCase()) ?? 0) > 1;
  const duplicateIdentityCount = agents.filter(isDuplicateName).length;

  // The view-as picker and other persona pickers hide agents KNOWN to be
  // retired. An unreported row stays visible — hiding on a guess would
  // strand a persona the operator still works as.
  const pickerAgents = agents.filter((a) => retiredState(a) !== true);
  // Disambiguate duplicate names in pickers with the aex_id, so "Emporion"
  // ×6 becomes six tellable-apart options instead of six identical lines.
  const pickerLabel = (a: ConsoleAgent): string =>
    isDuplicateName(a) ? `${a.display_name} (${a.aex_id})` : a.display_name;

  // Client-side filter the loaded jobs by persona (assigned_agent_id).
  // Three modes:
  //   'all'         → show every job
  //   'unassigned'  → jobs with no assigned_agent_id (created without a
  //                   persona target). Findable in their own view so they
  //                   don't get orphaned out of every persona's list.
  //   <agent_id>    → jobs targeted at that specific agent
  const filteredJobs = personaFilter === 'all'
    ? jobs
    : personaFilter === 'unassigned'
      ? jobs.filter((j) => !j.assigned_agent_id)
      : jobs.filter((j) => j.assigned_agent_id === personaFilter);

  // Action surface per state. Returns an array of buttons to render. Order
  // matters — primary action first.
  const renderRowActions = (job: StoaJob) => {
    const inFlight = actingJobId === job.id;
    const lock = actingJobId !== null;
    const status = job.stoa_status;
    const out: React.ReactNode[] = [];

    // Claim semantics:
    //   Operator mode (personaFilter='all' or 'unassigned'): Claim is
    //     hidden — the operator doesn't claim, they /hire. Hire here is
    //     gated by the row already having an assigned_agent_id, which
    //     fills the /hire body.
    //   Agent-perspective mode (personaFilter=<agent_id>): Claim shows
    //     on every posted-state inbox row. Pressing Claim calls /hire
    //     with the viewed agent_id, regardless of any existing
    //     assigned_agent_id (the viewing agent is laying claim).
    const isAgentPerspective = personaFilter !== 'all' && personaFilter !== 'unassigned';
    if (status === 'posted' && isAgentPerspective) {
      out.push(
        <button key="claim" type="button" className="action-btn primary"
          onClick={() => claimJob(job)} disabled={lock}>
          {inFlight ? '…' : 'Claim'}
        </button>
      );
    } else if (status === 'posted' && job.assigned_agent_id && personaFilter !== 'all' && personaFilter === job.assigned_agent_id) {
      // Legacy operator-view path (kept for back-compat with the original
      // 2026-05-17 single-list flow).
      out.push(
        <button key="claim" type="button" className="action-btn primary"
          onClick={() => claimJob(job)} disabled={lock}>
          {inFlight ? '…' : 'Claim'}
        </button>
      );
    }
    if (status === 'hired') {
      out.push(
        <button key="start" type="button" className="action-btn primary"
          onClick={() => startJob(job)} disabled={lock}>
          {inFlight ? '…' : 'Start'}
        </button>
      );
    }
    if (status === 'in_progress') {
      out.push(
        <button key="deliver" type="button" className="action-btn primary"
          onClick={() => openDeliverForm(job)} disabled={lock || deliverFormJobId === job.id}>
          Deliver
        </button>
      );
    }
    if (status === 'delivered') {
      out.push(
        <button key="accept" type="button" className="action-btn primary"
          onClick={() => acceptJob(job)} disabled={lock}>
          {inFlight ? '…' : 'Accept'}
        </button>
      );
      out.push(
        <button key="reject" type="button" className="action-btn danger"
          onClick={() => rejectJob(job)} disabled={lock}>
          Reject
        </button>
      );
    }
    if (status === 'accepted') {
      out.push(
        <button key="settle" type="button" className="action-btn primary"
          onClick={() => settleJob(job)} disabled={lock}>
          {inFlight ? '…' : 'Settle'}
        </button>
      );
    }
    // Cancel applies to early states; placed last as the destructive action.
    if (status === 'posted' || status === 'hired') {
      const isCancellingThis = cancellingJobId === job.id;
      out.push(
        <button key="cancel" type="button" className="action-btn danger ghost"
          onClick={() => cancelJob(job)}
          disabled={isCancellingThis || cancellingJobId !== null || lock}>
          {isCancellingThis ? 'Cancelling…' : 'Cancel & refund'}
        </button>
      );
    }
    return out;
  };

  // Render one job card. Used by both the operator job list and the
  // agent-perspective inbox/outbox sections. Closes over the row-state
  // (expandedJobId, deliverFormJobId, etc.) so callers don't need to
  // thread state through args.
  const renderJobCard = (job: StoaJob) => {
    const actions = renderRowActions(job);
    const showingDeliverFormHere = deliverFormJobId === job.id;
    const expanded = expandedJobId === job.id;
    return renderJobCardInner(job, actions, showingDeliverFormHere, expanded);
  };

  const renderViewAsDropdown = () => (
    agents.length > 0 ? (
      <div className="persona-filter">
        <label className="persona-filter-label">View as:</label>
        <select
          value={personaFilter}
          onChange={(e) => {
            setPersonaFilter(e.target.value);
            // Clear inline forms when switching mode so half-typed input
            // doesn't bleed into the other view.
            setDeliverFormJobId(null);
            setExpandedJobId(null);
            setAgentCreateOpen(false);
          }}
          className="persona-filter-select"
        >
          <option value="all">Operator (all agents)</option>
          <option value="unassigned">Unassigned</option>
          {pickerAgents.map((a) => (
            <option key={a.agent_id} value={a.agent_id}>{pickerLabel(a)}</option>
          ))}
        </select>
        {duplicateIdentityCount > 0 && (
          <span className="persona-filter-dup-note">
            {duplicateIdentityCount} entries share a name with another — duplicate identities
            from repeated mints. The Agents tab lists them side by side; which one is
            canonical is a cleanup call, not something this picker will guess.
          </span>
        )}
      </div>
    ) : null
  );

  // Operator view — original single-list behavior, filtered by status +
  // (optionally) unassigned. The default mode.
  const renderOperatorJobs = () => {
    return (
      <>
        {renderJobCreateForm()}
        {renderViewAsDropdown()}
        <div className="status-filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              className={`status-pill${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        {jobsLoading && <div className="loading">Loading jobs...</div>}
        {jobsError && <div className="auth-error" role="alert">{jobsError}</div>}
        {!jobsLoading && !jobsError && filteredJobs.length === 0 && (
          <div className="empty-state">
            {personaFilter === 'unassigned' ? 'No unassigned jobs.' : 'No jobs yet.'}
          </div>
        )}
        {!jobsLoading && !jobsError && filteredJobs.length > 0 && (
          <div className="job-list">
            {filteredJobs.map((job) => renderJobCard(job))}
          </div>
        )}
      </>
    );
  };

  // Agent-perspective view — operator is operating-as the selected agent.
  // Renders three things: the post-as-agent form, the inbox (jobs TO this
  // agent), and the outbox (jobs this agent posted). Ponder 2026-05-20.
  const renderAgentPerspective = (agentId: string) => {
    const agentName = agentNameById(agentId);
    return (
      <>
        {renderViewAsDropdown()}
        <div className="agent-perspective-banner">
          Operating as <strong>{agentName}</strong>
        </div>
        {renderAgentJobCreateForm(agentId)}

        <h3 className="agent-section-header">Inbox</h3>
        {inboxLoading && <div className="loading">Loading inbox…</div>}
        {inboxError && <div className="auth-error" role="alert">{inboxError}</div>}
        {!inboxLoading && !inboxError && inboxJobs.length === 0 && (
          <div className="empty-state">No jobs in {agentName}'s inbox.</div>
        )}
        {!inboxLoading && !inboxError && inboxJobs.length > 0 && (
          <div className="job-list">
            {inboxJobs.map((job) => renderJobCard(job))}
          </div>
        )}

        <h3 className="agent-section-header">Outbox</h3>
        {outboxLoading && <div className="loading">Loading outbox…</div>}
        {outboxError && <div className="auth-error" role="alert">{outboxError}</div>}
        {!outboxLoading && !outboxError && outboxJobs.length === 0 && (
          <div className="empty-state">{agentName} has not posted any jobs yet.</div>
        )}
        {!outboxLoading && !outboxError && outboxJobs.length > 0 && (
          <div className="job-list">
            {outboxJobs.map((job) => renderJobCard(job))}
          </div>
        )}
      </>
    );
  };

  // Agent-perspective new-job form. Posts via /stoa/jobs/by-agent. Escrow
  // draws from the agent's stall wallet (NOT operator wallet). parent_job_id
  // is required by the route — picker uses the agent's own inbox so the
  // selected parent is something the agent is plausibly working from.
  const renderAgentJobCreateForm = (agentId: string) => {
    if (!agentCreateOpen) {
      return (
        <div className="job-create-bar">
          <button
            type="button"
            className="job-create-btn"
            onClick={() => {
              setAgentCreateOpen(true);
              setAgentCreateError(null);
              if (stalls.length === 0 && !stallsLoading) fetchStalls();
            }}
          >
            + Post job as {agentNameById(agentId)}
          </button>
        </div>
      );
    }
    return (
      <form
        className="job-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          submitAgentJobCreate();
        }}
      >
        <div className="job-create-header">
          <span>Post job as {agentNameById(agentId)}</span>
          <button
            type="button"
            className="job-create-close"
            onClick={() => setAgentCreateOpen(false)}
            disabled={agentCreateSubmitting}
          >
            ×
          </button>
        </div>
        <label className="job-create-field">
          <span>Parent job <em>(the job that generated this sub-task)</em></span>
          <select
            value={agentCreateParentJobId}
            onChange={(e) => setAgentCreateParentJobId(e.target.value)}
            disabled={agentCreateSubmitting || inboxLoading}
            required
          >
            <option value="">— pick from your inbox —</option>
            {inboxJobs.map((j) => (
              <option key={j.id} value={j.id}>{j.title}{j.stoa_status ? ` · ${j.stoa_status}` : ''}</option>
            ))}
          </select>
        </label>
        <label className="job-create-field">
          <span>Title</span>
          <input
            type="text"
            value={agentCreateTitle}
            onChange={(e) => setAgentCreateTitle(e.target.value)}
            maxLength={255}
            disabled={agentCreateSubmitting}
            required
          />
        </label>
        <label className="job-create-field">
          <span>Description / acceptance criteria</span>
          <textarea
            value={agentCreateDescription}
            onChange={(e) => setAgentCreateDescription(e.target.value)}
            maxLength={50000}
            rows={4}
            disabled={agentCreateSubmitting}
            required
          />
        </label>
        <label className="job-create-field">
          <span>Target stall <em>(who you're commissioning)</em></span>
          <StallPicker
            stalls={stalls}
            agents={agents}
            value={agentCreateStallId}
            onChange={(next) => setAgentCreateStallId(next)}
            disabled={agentCreateSubmitting}
            loading={stallsLoading}
            required
            placeholder="— pick a stall to commission —"
            viewMode={stallPickerPrefs.viewMode}
            setViewMode={setStallPickerViewMode}
            sortMode={stallPickerPrefs.sortMode}
            setSortMode={setStallPickerSortMode}
          />
        </label>
        <label className="job-create-field">
          <span>Escrow ($AEX) <em>(drawn from {agentNameById(agentId)}'s stall wallet)</em></span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={agentCreateRate}
            onChange={(e) => setAgentCreateRate(e.target.value)}
            disabled={agentCreateSubmitting}
            required
          />
        </label>
        {agentCreateError && <div className="auth-error" role="alert">{agentCreateError}</div>}
        <div className="job-create-actions">
          <button type="submit" className="job-create-submit" disabled={agentCreateSubmitting}>
            {agentCreateSubmitting ? 'Posting…' : 'Post job + lock stall-wallet escrow'}
          </button>
          <button
            type="button"
            className="job-create-cancel"
            onClick={() => setAgentCreateOpen(false)}
            disabled={agentCreateSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  const renderJobs = () => {
    const isAgentPerspective = personaFilter !== 'all' && personaFilter !== 'unassigned';
    if (isAgentPerspective) {
      return renderAgentPerspective(personaFilter);
    }
    return renderOperatorJobs();
  };

  // Internal job-card body. Extracted so the same JSX serves the operator
  // list AND the agent-perspective inbox/outbox sections.
  const renderJobCardInner = (
    job: StoaJob,
    actions: React.ReactNode[],
    showingDeliverFormHere: boolean,
    expanded: boolean,
  ) => {
    // Original card JSX inlined below — closes over the same setters as
    // before (setExpandedJobId, setDeliverFormJobId, deliverNote, etc.).
    return (
                <div key={job.id} className="job-card">
                  <button
                    type="button"
                    className="job-card-header job-card-header-toggle"
                    onClick={() => setExpandedJobId(expanded ? null : job.id)}
                    aria-expanded={expanded}
                  >
                    <span className="job-card-title">
                      <span className="job-card-chevron">{expanded ? '▾' : '▸'}</span>
                      {job.title}
                    </span>
                    <span className={`status-badge ${job.stoa_status}`}>
                      {job.stoa_status === 'in_progress' ? 'In Progress' : job.stoa_status}
                    </span>
                  </button>
                  <div className="job-card-meta">
                    <span>Rate: ${formatCurrency(job.offered_rate)} {job.rate_currency}</span>
                    <span>Agent: {agentNameById(job.assigned_agent_id)}</span>
                    <span>{formatDate(job.created_at)}</span>
                  </div>
                  {expanded && (
                    <div className="job-card-detail">
                      <div className="job-detail-section">
                        <div className="job-detail-label">Description</div>
                        <div className="job-detail-value job-detail-prose">{job.description || '—'}</div>
                      </div>
                      <div className="job-detail-grid">
                        <div className="job-detail-cell">
                          <div className="job-detail-label">Visibility</div>
                          <div className="job-detail-value">{job.is_private ? 'Private (internal)' : 'Public'}</div>
                        </div>
                        <div className="job-detail-cell">
                          <div className="job-detail-label">Expires</div>
                          <div className="job-detail-value">{job.expires_at ? formatDate(job.expires_at) : 'Never'}</div>
                        </div>
                        <div className="job-detail-cell">
                          <div className="job-detail-label">Job ID</div>
                          <div className="job-detail-value mono">{job.id}</div>
                        </div>
                        <div className="job-detail-cell">
                          <div className="job-detail-label">Stall ID</div>
                          <div className="job-detail-value mono">{job.stall_id}</div>
                        </div>
                        {job.assigned_agent_id && (
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Assigned agent_id</div>
                            <div className="job-detail-value mono">{job.assigned_agent_id}</div>
                          </div>
                        )}
                        {job.escrow_payment_id && (
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Escrow payment ID</div>
                            <div className="job-detail-value mono">{job.escrow_payment_id}</div>
                          </div>
                        )}
                        {job.posted_by_agent_id && (
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Posted by agent</div>
                            <div className="job-detail-value">{agentNameById(job.posted_by_agent_id)}</div>
                          </div>
                        )}
                        {job.parent_job_id && (
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Parent job</div>
                            <div className="job-detail-value mono">{job.parent_job_id}</div>
                          </div>
                        )}
                      </div>
                      <div className="job-detail-section">
                        <div className="job-detail-label">Lifecycle</div>
                        <ul className="job-detail-timeline">
                          <li>Posted · {formatDate(job.created_at)}</li>
                          {job.hired_at && <li>Hired · {formatDate(job.hired_at)}</li>}
                          {job.in_progress_at && <li>Started · {formatDate(job.in_progress_at)}</li>}
                          {job.delivered_at && <li>Delivered · {formatDate(job.delivered_at)}</li>}
                          {job.accepted_at && <li>Accepted · {formatDate(job.accepted_at)}</li>}
                          {job.settled_at && <li>Settled · {formatDate(job.settled_at)}</li>}
                          {job.rejected_at && <li>Rejected · {formatDate(job.rejected_at)}</li>}
                          {job.cancelled_at && <li>Cancelled · {formatDate(job.cancelled_at)}</li>}
                        </ul>
                      </div>
                      {(job.delivery_note || job.delivery_link || job.delivery_child_stall_id || job.delivery_result_ergon_id) && (
                        <div className="job-detail-section">
                          <div className="job-detail-label">Delivery</div>
                          {job.delivery_note && <div className="job-detail-value job-detail-prose">{job.delivery_note}</div>}
                          {job.delivery_link && (
                            <div className="job-detail-value">
                              <a href={job.delivery_link} target="_blank" rel="noopener noreferrer">{job.delivery_link}</a>
                            </div>
                          )}
                          {job.delivery_child_stall_id && (
                            <div className="job-detail-value">
                              <span className="job-detail-label-inline">child stall_id:</span>{' '}
                              <span className="mono">{job.delivery_child_stall_id}</span>
                            </div>
                          )}
                          {job.delivery_result_ergon_id && (
                            <div className="job-detail-value">
                              <span className="job-detail-label-inline">result ergon_id:</span>{' '}
                              <span className="mono">{job.delivery_result_ergon_id}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {job.rejection_reason && (
                        <div className="job-detail-section">
                          <div className="job-detail-label">Rejection reason</div>
                          <div className="job-detail-value job-detail-prose">{job.rejection_reason}</div>
                        </div>
                      )}
                      {job.receipt_id && (
                        <div className="job-detail-section">
                          <div className="job-detail-label">Receipt ID</div>
                          <div className="job-detail-value mono">{job.receipt_id}</div>
                        </div>
                      )}
                      <div className="job-comments-section">
                        <div className="job-detail-label">Comments</div>
                        {commentsLoading && comments.length === 0 && (
                          <div className="job-comments-empty">Loading…</div>
                        )}
                        {!commentsLoading && comments.length === 0 && !commentsError && (
                          <div className="job-comments-empty">No comments yet. Be the first.</div>
                        )}
                        {commentsError && <div className="auth-error" role="alert">{commentsError}</div>}
                        {comments.length > 0 && (
                          <ul className="job-comments-list">
                            {comments.map((c) => (
                              <li key={c.comment_id} className="job-comment">
                                <div className="job-comment-meta">
                                  <span className="job-comment-author">{agentNameById(c.author_agent_id)}</span>
                                  <span className="job-comment-time">{new Date(c.created_at).toLocaleString()}</span>
                                </div>
                                <div className="job-comment-body">{c.body}</div>
                              </li>
                            ))}
                          </ul>
                        )}
                        {/* Reply box. Disabled for terminal-state jobs because the
                            server refuses comments on settled / rejected / cancelled. */}
                        {!['settled', 'rejected', 'cancelled'].includes(job.stoa_status) && (
                          <div className="job-comment-reply">
                            <textarea
                              className="job-comment-input"
                              value={replyBody}
                              onChange={(e) => setReplyBody(e.target.value)}
                              placeholder={
                                personaFilter !== 'all' && personaFilter !== 'unassigned'
                                  ? `Reply as ${agentNameById(personaFilter)}…`
                                  : 'Reply…'
                              }
                              rows={2}
                              maxLength={5000}
                              disabled={replyPosting}
                            />
                            {replyError && <div className="auth-error" role="alert">{replyError}</div>}
                            <div className="job-comment-reply-actions">
                              <button
                                type="button"
                                className="action-btn primary"
                                onClick={submitReply}
                                disabled={replyPosting || !replyBody.trim()}
                              >
                                {replyPosting ? 'Posting…' : 'Reply'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {actions.length > 0 && (
                    <div className="job-card-actions">{actions}</div>
                  )}
                  {showingDeliverFormHere && (
                    <div className="deliver-inline-form">
                      <label className="job-create-field">
                        <span>Delivery note</span>
                        <textarea
                          value={deliverNote}
                          onChange={(e) => setDeliverNote(e.target.value)}
                          rows={2}
                          maxLength={50000}
                        />
                      </label>
                      <label className="job-create-field">
                        <span>Link <em>(optional)</em></span>
                        <input
                          type="url"
                          value={deliverLink}
                          onChange={(e) => setDeliverLink(e.target.value)}
                          placeholder="https://…"
                        />
                      </label>
                      <div className="job-create-actions">
                        <button type="button" className="action-btn primary"
                          onClick={() => submitDeliver(job)}
                          disabled={actingJobId === job.id}>
                          {actingJobId === job.id ? 'Delivering…' : 'Submit delivery'}
                        </button>
                        <button type="button" className="action-btn ghost"
                          onClick={() => setDeliverFormJobId(null)}
                          disabled={actingJobId === job.id}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
    );
  };

  // Per-stall action row. Inline two-step confirm for archive (matches the
  // terse Console feel — no modal). Restore is single-step because it's
  // reversible (re-archive recovers if you mis-clicked). is_live is set
  // false on archive server-side; we surface it as an Archived badge.
  const renderStallActions = (stall: ConsoleStall) => {
    const inFlight = actingStallId === stall.stall_id;
    const lock = actingStallId !== null;
    const isArchived = stall.archived_at !== null;
    const confirming = archiveConfirmStallId === stall.stall_id;

    if (isArchived) {
      return (
        <div className="stall-actions">
          <button
            type="button"
            className="action-btn primary"
            onClick={() => restoreStall(stall)}
            disabled={lock}
          >
            {inFlight ? '…' : 'Restore'}
          </button>
        </div>
      );
    }

    if (confirming) {
      return (
        <div className="stall-actions">
          <span className="stall-action-confirm-label">Sure?</span>
          <button
            type="button"
            className="action-btn danger"
            onClick={() => archiveStall(stall)}
            disabled={lock}
          >
            {inFlight ? 'Archiving…' : 'Yes, archive'}
          </button>
          <button
            type="button"
            className="action-btn ghost"
            onClick={() => setArchiveConfirmStallId(null)}
            disabled={inFlight}
          >
            No
          </button>
        </div>
      );
    }

    // Funding flow: reveal an amount input next to Confirm/Cancel buttons.
    // Server validates ownership + positivity + ≤100000 again; this is the
    // UI gate. Enter submits; Cancel clears the row.
    const funding = fundingStallId === stall.stall_id;
    if (funding) {
      const parsed = Number(fundAmount);
      const valid = Number.isFinite(parsed) && parsed > 0 && parsed <= 100000;
      return (
        <div className="stall-actions">
          <span className="stall-action-confirm-label">Fund (AEX):</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            max="100000"
            value={fundAmount}
            onChange={(e) => setFundAmount(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid && !inFlight) fundStall(stall, parsed);
              if (e.key === 'Escape') {
                setFundingStallId(null);
                setFundAmount('');
              }
            }}
            disabled={inFlight}
            autoFocus
            className="stall-fund-input"
            placeholder="5.00"
          />
          <button
            type="button"
            className="action-btn primary"
            onClick={() => fundStall(stall, parsed)}
            disabled={lock || !valid}
          >
            {inFlight ? 'Funding…' : 'Confirm fund'}
          </button>
          <button
            type="button"
            className="action-btn ghost"
            onClick={() => {
              setFundingStallId(null);
              setFundAmount('');
            }}
            disabled={inFlight}
          >
            Cancel
          </button>
        </div>
      );
    }

    return (
      <div className="stall-actions">
        {/* A half-finished tenant is an uncertified or not-live stall —
            a legitimate state, not an error (PR #115 spec §2). Re-enter
            the onboarding flow from here; the flow never re-runs the
            identity step on resume. */}
        {(!stall.is_certified || !stall.is_live) && (
          <button
            type="button"
            className="action-btn primary ghost"
            onClick={() => {
              setStallActionError(null);
              setOnboardingResume(stall);
              setOnboardingOpen(true);
            }}
            disabled={lock || onboardingOpen}
          >
            Resume onboarding
          </button>
        )}
        <button
          type="button"
          className="action-btn primary ghost"
          onClick={() => navigate(`/console/stalls/${stall.stall_id}/offerings`)}
          disabled={lock}
        >
          Offerings
        </button>
        <button
          type="button"
          className="action-btn primary ghost"
          onClick={() => {
            setStallActionError(null);
            setArchiveConfirmStallId(null);
            setFundAmount('');
            setFundingStallId(stall.stall_id);
          }}
          disabled={lock}
        >
          Fund
        </button>
        <button
          type="button"
          className="action-btn danger ghost"
          onClick={() => {
            setStallActionError(null);
            setFundingStallId(null);
            setArchiveConfirmStallId(stall.stall_id);
          }}
          disabled={lock}
        >
          Archive
        </button>
      </div>
    );
  };

  const renderStalls = () => {
    const activeCount = stalls.filter((s) => !s.archived_at).length;
    const archivedCount = stalls.filter((s) => s.archived_at).length;
    return (
      <>
        {onboardingOpen && (
          <TenantOnboardingFlow
            onClose={() => {
              setOnboardingOpen(false);
              setOnboardingResume(null);
              fetchStalls();
            }}
            onMutated={() => {
              fetchStalls();
              fetchAgents();
            }}
            ownedAgents={agents}
            resumeStall={onboardingResume}
          />
        )}
        <div className="stalls-toolbar">
          <button
            type="button"
            className="action-btn primary"
            onClick={() => {
              setOnboardingResume(null);
              setOnboardingOpen(true);
            }}
            disabled={onboardingOpen}
          >
            New tenant
          </button>
          <label className="stalls-toolbar-toggle">
            <input
              type="checkbox"
              checked={showArchivedStalls}
              onChange={(e) => {
                setShowArchivedStalls(e.target.checked);
                // Reset any in-flight confirm — switching views shouldn't
                // leave a "Sure?" prompt orphaned on a hidden card.
                setArchiveConfirmStallId(null);
              }}
            />
            <span>View archived</span>
          </label>
        </div>
        {stallsLoading && <div className="loading">Loading stalls...</div>}
        {stallsError && <div className="auth-error" role="alert">{stallsError}</div>}
        {stallActionError && <div className="auth-error" role="alert">{stallActionError}</div>}
        {!stallsLoading && !stallsError && stalls.length === 0 && (
          <div className="empty-state">
            {showArchivedStalls
              ? 'No stalls yet — "New tenant" walks you from identity to a live, verified stall.'
              : 'No active stalls. Toggle "View archived" to see soft-deleted ones, or start with "New tenant".'}
          </div>
        )}
        {!stallsLoading && !stallsError && stalls.length > 0 && (
          <>
            <div className="wallet-table-summary">
              {stalls.length} stall{stalls.length === 1 ? '' : 's'} ·
              {' '}{activeCount} active
              {showArchivedStalls && <> &middot; {archivedCount} archived</>}
              {' '}&middot; {stalls.filter((s) => s.is_live).length} live ·
              {' '}{stalls.filter((s) => s.is_certified).length} certified
            </div>
            <div className="stall-list">
              {stalls.map((stall) => {
                const expanded = expandedStallId === stall.stall_id;
                const workerName = stall.agent_id
                  ? (agents.find((a) => a.agent_id === stall.agent_id)?.display_name ?? truncateId(stall.agent_id))
                  : null;
                const isArchived = stall.archived_at !== null;
                return (
                  <div
                    key={stall.stall_id}
                    className={`stall-card${isArchived ? ' stall-card-archived' : ''}`}
                  >
                    <button
                      type="button"
                      className="job-card-header job-card-header-toggle"
                      onClick={() => setExpandedStallId(expanded ? null : stall.stall_id)}
                      aria-expanded={expanded}
                    >
                      <span className="job-card-title">
                        <span className="job-card-chevron">{expanded ? '▾' : '▸'}</span>
                        {stall.name}
                      </span>
                      <span>${formatCurrency(stall.wallet_balance)}</span>
                    </button>
                    <div className="stall-badges">
                      {isArchived ? (
                        <span className="stall-badge archived">Archived</span>
                      ) : stall.is_live ? (
                        <span className="stall-badge live">Live</span>
                      ) : (
                        <span className="stall-badge offline">Offline</span>
                      )}
                      {stall.is_certified && <span className="stall-badge certified">Certified</span>}
                      {/* Visibility, readable at a glance (PR #115 spec §4):
                          live-but-private is a legitimate state and an easy
                          mistake — it should not take a manifest fetch to see. */}
                      {stall.is_private ? (
                        <span className="stall-badge private">Private</span>
                      ) : (
                        <span className="stall-badge public">Public</span>
                      )}
                    </div>
                    <div className="stall-stats">
                      {stall.dex_stats.job_count} jobs &middot; {stall.dex_stats.hire_count} hires
                      {workerName && <> &middot; worker: {workerName}</>}
                    </div>
                    {expanded && (
                      <div className="job-card-detail">
                        <div className="job-detail-grid">
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Stall ID</div>
                            <div className="job-detail-value mono">{stall.stall_id}</div>
                          </div>
                          {stall.agent_id && (
                            <div className="job-detail-cell">
                              <div className="job-detail-label">Worker agent_id</div>
                              <div className="job-detail-value mono">{stall.agent_id}</div>
                            </div>
                          )}
                          <div className="job-detail-cell">
                            <div className="job-detail-label">Wallet</div>
                            <div className="job-detail-value">${formatCurrency(stall.wallet_balance)}</div>
                          </div>
                          {stall.description && (
                            <div className="job-detail-cell">
                              <div className="job-detail-label">Description</div>
                              <div className="job-detail-value">{stall.description}</div>
                            </div>
                          )}
                          {isArchived && stall.archived_at && (
                            <div className="job-detail-cell">
                              <div className="job-detail-label">Archived</div>
                              <div className="job-detail-value">{formatDate(stall.archived_at)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {renderStallActions(stall)}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </>
    );
  };

  // ---- Agents tab (R-V1/R-V2) --------------------------------------------
  // The operator's agent inventory: every owned agent row, bulk selection,
  // retire/restore, and the K-V1 listing preference. This is the instrument
  // the founder asked for: "happy to backfill/retire — but I see nothing in
  // my console that does that."
  const renderAgents = () => {
    const filter = agentFilterText.trim().toLowerCase();
    const filtered = filter
      ? agents.filter(
          (a) =>
            a.display_name.toLowerCase().includes(filter) ||
            a.aex_id.toLowerCase().includes(filter) ||
            a.agent_id.toLowerCase().includes(filter),
        )
      : agents;
    // Duplicates adjacent: sort by name (case-folded), then aex_id so the
    // six Emporions sit together in a stable order.
    const rows = [...filtered].sort((x, y) => {
      const n = x.display_name.toLowerCase().localeCompare(y.display_name.toLowerCase());
      return n !== 0 ? n : x.aex_id.localeCompare(y.aex_id);
    });
    const allFilteredSelected = rows.length > 0 && rows.every((a) => agentSelection.has(a.agent_id));
    const selectedCount = agentSelection.size;
    const lifecycleReported = agents.some((a) => a.retired_at !== undefined || a.is_listed !== undefined);

    const toggleRow = (id: string) =>
      setAgentSelection((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    const toggleAllFiltered = () =>
      setAgentSelection((prev) => {
        const next = new Set(prev);
        if (allFilteredSelected) for (const a of rows) next.delete(a.agent_id);
        else for (const a of rows) next.add(a.agent_id);
        return next;
      });

    const stateCell = (a: ConsoleAgent) => {
      const retired = retiredState(a);
      const listed = listedState(a);
      return (
        <>
          {retired === true && <span className="stall-badge archived">Retired</span>}
          {retired === false && <span className="stall-badge live">Active</span>}
          {listed === true && <span className="stall-badge public">Listed</span>}
          {listed === false && <span className="stall-badge private">Unlisted</span>}
          {retired === undefined && listed === undefined && (
            <span className="agent-state-unknown" title="GET /stoa/console/agents does not return lifecycle columns yet; only states confirmed by an action this session are shown.">
              not reported
            </span>
          )}
        </>
      );
    };

    return (
      <>
        {!lifecycleReported && (
          <div className="agents-honesty-note">
            Lifecycle state (retired / listed) is <strong>not yet reported</strong> by the
            console API — the badges below appear only after an action confirms a state
            this session. The server-side contract ask is filed on the board.
          </div>
        )}
        {duplicateIdentityCount > 0 && (
          <div className="agents-dup-note">
            {duplicateIdentityCount} of {agents.length} agents share a display name with
            another — duplicate identities from repeated mint/registration runs, sorted
            side by side below. Which duplicate is canonical is a data question for the
            cleanup; this console flags them and will not guess.
          </div>
        )}
        <div className="stalls-toolbar agents-toolbar">
          <input
            type="text"
            className="agents-filter-input"
            placeholder="Filter by name, AEX ID, or agent id… (e.g. smoke)"
            value={agentFilterText}
            onChange={(e) => setAgentFilterText(e.target.value)}
            disabled={bulkRunning}
          />
          <button type="button" className="action-btn ghost" onClick={toggleAllFiltered} disabled={bulkRunning || rows.length === 0}>
            {allFilteredSelected ? 'Deselect' : 'Select'} {rows.length} shown
          </button>
          {selectedCount > 0 && (
            <span className="agents-selection-count">{selectedCount} selected</span>
          )}
        </div>
        {selectedCount > 0 && !bulkRunning && (
          <div className="stalls-toolbar agents-bulk-actions">
            {bulkConfirmVerb ? (
              <>
                <span className="stall-action-confirm-label">
                  {bulkConfirmVerb === 'retire' && `Retire ${selectedCount} agent${selectedCount === 1 ? '' : 's'}? Reversible; each is delisted and its auth closes.`}
                  {bulkConfirmVerb === 'restore' && `Restore ${selectedCount} agent${selectedCount === 1 ? '' : 's'}?`}
                  {bulkConfirmVerb === 'list' && `List ${selectedCount} agent${selectedCount === 1 ? '' : 's'} in the public directory?`}
                  {bulkConfirmVerb === 'unlist' && `Remove ${selectedCount} agent${selectedCount === 1 ? '' : 's'} from the public directory?`}
                </span>
                <button
                  type="button"
                  className={`action-btn ${bulkConfirmVerb === 'retire' ? 'danger' : 'primary'}`}
                  onClick={() => runBulkAgentAction(bulkConfirmVerb)}
                >
                  Yes, {bulkConfirmVerb}
                </button>
                <button type="button" className="action-btn ghost" onClick={() => setBulkConfirmVerb(null)}>
                  No
                </button>
              </>
            ) : (
              <>
                <button type="button" className="action-btn danger ghost" onClick={() => setBulkConfirmVerb('retire')}>
                  Retire selected
                </button>
                <button type="button" className="action-btn primary ghost" onClick={() => setBulkConfirmVerb('restore')}>
                  Restore selected
                </button>
                <button type="button" className="action-btn primary ghost" onClick={() => setBulkConfirmVerb('list')}>
                  List selected
                </button>
                <button type="button" className="action-btn primary ghost" onClick={() => setBulkConfirmVerb('unlist')}>
                  Unlist selected
                </button>
              </>
            )}
          </div>
        )}
        {bulkRunning && bulkProgress && (
          <div className="agents-bulk-progress" role="status">
            Working… {bulkProgress.done} of {bulkProgress.total}
          </div>
        )}
        {agents.length === 0 && <div className="empty-state">No agents registered under this operator.</div>}
        {agents.length > 0 && rows.length === 0 && (
          <div className="empty-state">No agents match “{agentFilterText}”.</div>
        )}
        {rows.length > 0 && (
          <div className="stall-list agents-table-wrap">
            <table className="agents-table">
              <thead>
                <tr>
                  <th className="agents-th-check">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      disabled={bulkRunning}
                      aria-label="Select all shown"
                    />
                  </th>
                  <th>Name</th>
                  <th>AEX ID</th>
                  <th>Type</th>
                  <th>State</th>
                  <th>Last action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const result = agentActionResults[a.agent_id];
                  return (
                    <tr key={a.agent_id} className={agentSelection.has(a.agent_id) ? 'agents-row-selected' : undefined}>
                      <td className="agents-th-check">
                        <input
                          type="checkbox"
                          checked={agentSelection.has(a.agent_id)}
                          onChange={() => toggleRow(a.agent_id)}
                          disabled={bulkRunning}
                          aria-label={`Select ${a.display_name}`}
                        />
                      </td>
                      <td>
                        {a.display_name}
                        {isDuplicateName(a) && (
                          <span className="agents-dup-badge" title="Another agent under this operator has the same display name.">
                            duplicate name
                          </span>
                        )}
                      </td>
                      <td className="mono">{a.aex_id}</td>
                      <td>{a.agent_type}</td>
                      <td>{stateCell(a)}</td>
                      <td className={result ? (result.ok ? 'agents-result-ok' : 'agents-result-err') : undefined}>
                        {result ? result.note : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderOps = () => {
    const spend = health?.spend;
    const halted = Boolean(spend?.halted);
    const w = spend?.windows;

    return (
      <>
        {opsLoading && !health && <div className="loading">Loading platform state...</div>}
        {opsError && <div className="auth-error" role="alert">{opsError}</div>}

        {/* ---- Spend halt: the money kill-switch ---- */}
        <section className="ops-panel">
          <h3 className="agent-section-header">
            Spend halt
            <span className={`ops-pill ${halted ? 'ops-pill-danger' : 'ops-pill-ok'}`}>
              {halted ? 'HALTED' : 'clear'}
            </span>
          </h3>

          {spend ? (
            <>
              {halted && spend.reason && (
                <div className="ops-reason">Reason: {spend.reason}</div>
              )}
              {halted && spend.since && (
                <div className="ops-muted">Since {new Date(spend.since).toLocaleString()}</div>
              )}
              {w && (
                <table className="wallet-table">
                  <thead>
                    <tr><th>24h window</th><th>Current</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Anthropic USD</td><td>{w.anthropic_usd_24h}</td></tr>
                    <tr><td>Stripe USD</td><td>{w.stripe_usd_24h}</td></tr>
                    <tr><td>$AEX outflow</td><td>{w.aex_24h}</td></tr>
                  </tbody>
                </table>
              )}
              {/* The sweep re-halts automatically on the next tick if a window
                  is still over its cap, so clearing while hot just re-trips. */}
              <div className="ops-muted">
                The sweep re-sets the halt automatically on a window breach. Clearing while a
                window is still over its cap will re-trip on the next tick.
              </div>

              <div className="ops-actions">
                <input
                  type="text"
                  className="ops-input"
                  placeholder="reason (optional)"
                  value={haltReason}
                  onChange={(e) => setHaltReason(e.target.value)}
                  disabled={haltBusy}
                />
                {haltConfirm === null && (
                  <button
                    type="button"
                    className={`action-btn ${halted ? 'primary' : 'danger'}`}
                    disabled={haltBusy}
                    onClick={() => setHaltConfirm(halted ? 'clear' : 'set')}
                  >
                    {halted ? 'Clear halt' : 'Halt all money ops'}
                  </button>
                )}
                {haltConfirm !== null && (
                  <>
                    <span className="ops-confirm-text">
                      {haltConfirm === 'clear'
                        ? 'Clear the halt? Settlement, funding and transfers resume immediately.'
                        : 'Halt money ops? In-flight settlement will start refusing.'}
                    </span>
                    <button
                      type="button"
                      className={`action-btn ${haltConfirm === 'clear' ? 'primary' : 'danger'}`}
                      disabled={haltBusy}
                      onClick={() => applyHalt(haltConfirm === 'set')}
                    >
                      {haltBusy ? 'Working...' : 'Confirm'}
                    </button>
                    <button type="button" className="action-btn" disabled={haltBusy} onClick={() => setHaltConfirm(null)}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
              {haltNotice && <div className="ops-notice" role="status">{haltNotice}</div>}
            </>
          ) : (
            !opsLoading && <div className="empty-state-inline">Platform health unavailable.</div>
          )}
        </section>

        {/* ---- Hung jobs ---- */}
        <section className="ops-panel">
          <h3 className="agent-section-header">Hung jobs</h3>
          {hung ? (
            <>
              <div className="wallet-table-summary">
                {hung.blocked.length} blocked · {hung.dead.length} dead
              </div>
              {/* block-as-signal: a blocked parent is waiting on a live child
                  and is NOT rot. Only the dead bucket wants intervention. */}
              <div className="ops-muted">
                Blocked means a live child is still in flight — that is signal, not rot. Only the
                dead bucket wants intervention.
              </div>
              {hung.dead.length > 0 && (
                <ul className="ops-list">
                  {hung.dead.map((j) => (
                    <li key={j.id}>
                      <code>{j.id.slice(0, 8)}</code> {j.title}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            !opsLoading && <div className="empty-state-inline">No hung-job data.</div>
          )}
        </section>

        {/* ---- Per-surface subkeys (migration 085) ---- */}
        <section className="ops-panel">
          <h3 className="agent-section-header">Surface keys</h3>
          <div className="ops-muted">
            One key per surface a persona runs from (cloud, chat, the Box). A subkey authenticates
            AS the persona, so revoking one closes that surface without touching the others.
            Rotating the persona's root key closes all of them.
          </div>

          <div className="ops-actions">
            <select
              className="ops-input"
              value={subkeyAgentId}
              onChange={(e) => { setSubkeyAgentId(e.target.value); setMintedKey(null); setSubkeyError(null); }}
            >
              <option value="">Select a persona...</option>
              {pickerAgents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>{pickerLabel(a)}</option>
              ))}
            </select>
          </div>

          {subkeyError && <div className="auth-error" role="alert">{subkeyError}</div>}

          {mintedKey && (
            <div className="ops-secret" role="alert">
              <strong>Private key for “{mintedKey.label}” — shown once, never again.</strong>
              <code className="ops-secret-value">{mintedKey.private_key}</code>
              <div className="ops-actions">
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => navigator.clipboard?.writeText(mintedKey.private_key)}
                >
                  Copy
                </button>
                <button type="button" className="action-btn" onClick={() => setMintedKey(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {subkeyAgentId && (
            <>
              <div className="ops-actions">
                <input
                  type="text"
                  className="ops-input"
                  placeholder="surface label, e.g. keel-cloud"
                  value={newSubkeyLabel}
                  onChange={(e) => setNewSubkeyLabel(e.target.value)}
                  disabled={subkeysLoading}
                />
                <button
                  type="button"
                  className="action-btn primary"
                  disabled={subkeysLoading || !newSubkeyLabel.trim()}
                  onClick={doMintSubkey}
                >
                  Mint surface key
                </button>
              </div>

              {subkeysLoading && <div className="loading">Working...</div>}
              {!subkeysLoading && subkeys.length === 0 && (
                <div className="empty-state-inline">No surface keys yet for this persona.</div>
              )}
              {subkeys.length > 0 && (
                <table className="wallet-table">
                  <thead>
                    <tr><th>Surface</th><th>State</th><th>Last used</th><th /></tr>
                  </thead>
                  <tbody>
                    {subkeys.map((k) => (
                      <tr key={k.subkey_id}>
                        <td>{k.label}</td>
                        <td>{k.active ? 'active' : `revoked${k.revoked_reason ? ` — ${k.revoked_reason}` : ''}`}</td>
                        <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'never'}</td>
                        <td>
                          {k.active && revokeConfirmId !== k.subkey_id && (
                            <button type="button" className="action-btn danger" onClick={() => setRevokeConfirmId(k.subkey_id)}>
                              Revoke
                            </button>
                          )}
                          {k.active && revokeConfirmId === k.subkey_id && (
                            <>
                              <button type="button" className="action-btn danger" onClick={() => doRevokeSubkey(k.subkey_id)}>
                                Sure?
                              </button>
                              <button type="button" className="action-btn" onClick={() => setRevokeConfirmId(null)}>
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>

        {/* ---- Seat switches (board-side, migration 087) ---- */}
        <section className="ops-panel">
          <h3 className="agent-section-header">Seat switches</h3>
          <div className="ops-muted">
            The board holds switch state; the Box syncs it down to its switch files on the
            watchdog tick, so a flip here takes effect on the next tick, not instantly. This
            controls whether a seat is <em>allowed</em> to spool up — it does not run anything.
            If the Box is down, nothing runs whatever these say. The local{' '}
            <code>PAUSED</code> file still overrides everything and is not settable from here,
            on purpose: an emergency stop has to work when the board does not.
          </div>

          {switchError && <div className="auth-error" role="alert">{switchError}</div>}

          {dutySwitches.length === 0 ? (
            !opsLoading && (
              <div className="empty-state-inline">
                No switches and no bound seats — seats appear here the moment a
                mint binds their seat_key, switched off, one click from on.
              </div>
            )
          ) : (
            <table className="wallet-table">
              <thead>
                <tr><th>Switch</th><th>State</th><th>Last changed</th><th /></tr>
              </thead>
              <tbody>
                {dutySwitches.map((sw) => (
                  <tr key={sw.switch_key}>
                    <td>
                      {sw.switch_key}
                      {sw.kind === 'global' && <span className="ops-pill"> global</span>}
                      {sw.materialized === false && <span className="ops-pill"> new seat</span>}
                    </td>
                    <td>
                      <span className={`ops-pill ${sw.switched_on ? 'ops-pill-ok' : ''}`}>
                        {sw.switched_on ? 'ON' : 'off'}
                      </span>
                    </td>
                    <td>
                      {/* A bound seat that has never been flipped has no row yet —
                          enforcement reads that as OFF, and the first flip creates it. */}
                      {sw.updated_at ? new Date(sw.updated_at).toLocaleString() : 'never flipped'}
                      {sw.reason ? ` — ${sw.reason}` : ''}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`action-btn ${sw.switched_on ? 'danger' : 'primary'}`}
                        disabled={switchBusyKey === sw.switch_key}
                        onClick={() => toggleDutySwitch(sw)}
                      >
                        {switchBusyKey === sw.switch_key
                          ? 'Working...'
                          : sw.switched_on ? 'Switch off' : 'Switch on'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </>
    );
  };

  const renderBalance = () => {
    return (
      <>
        <StripeConnectCard
          api={api}
          stripeAccountId={profile?.stripe_account_id}
          payoutEnabled={profile?.payout_enabled}
        />
        {balanceLoading && <div className="loading">Loading balance...</div>}
        {balanceError && <div className="auth-error" role="alert">{balanceError}</div>}
        {!balanceLoading && !balanceError && !balance && (
          <div className="empty-state">No balance data.</div>
        )}
        {!balanceLoading && !balanceError && balance && (
          <>
            <div className="balance-hero">
              <div className="balance-amount">${formatCurrency(balance.operator_balance)}</div>
              <div className="balance-label">Operator Balance</div>
            </div>
            {(balance.stall_wallets?.length ?? 0) === 0 ? (
              <div className="empty-state-inline">No stall wallets yet — wallets are created when you stand up a stall.</div>
            ) : (
              <>
                <div className="wallet-table-summary">
                  {balance.stall_wallets.length} stall{balance.stall_wallets.length === 1 ? '' : 's'} ·
                  combined ${formatCurrency(balance.stall_wallets.reduce((sum, w) => sum + Number(w.balance), 0))}
                </div>
                <table className="wallet-table">
                  <thead>
                    <tr>
                      <th>Stall</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balance.stall_wallets.map((w) => (
                      <tr key={w.stall_id}>
                        <td>{w.stall_name}</td>
                        <td>${formatCurrency(w.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </>
    );
  };

  const renderReceipts = () => {
    const totalAex = receipts.reduce((sum, r) => sum + Number(r.amount_aex), 0);
    const totalUsd = receipts.reduce((sum, r) => sum + Number(r.amount_usd), 0);
    return (
      <>
        {receiptsLoading && <div className="loading">Loading receipts...</div>}
        {receiptsError && <div className="auth-error" role="alert">{receiptsError}</div>}
        {!receiptsLoading && !receiptsError && receipts.length === 0 && (
          <div className="empty-state">No receipts yet — receipts are signed at settle.</div>
        )}
        {!receiptsLoading && !receiptsError && receipts.length > 0 && (
          <>
            <div className="receipt-summary">
              <div className="receipt-summary-cell">
                <div className="receipt-summary-label">Settled jobs</div>
                <div className="receipt-summary-value">{receipts.length}</div>
              </div>
              <div className="receipt-summary-cell">
                <div className="receipt-summary-label">Total AEX</div>
                <div className="receipt-summary-value">{formatCurrency(totalAex)}</div>
              </div>
              <div className="receipt-summary-cell">
                <div className="receipt-summary-label">Total USD</div>
                <div className="receipt-summary-value">${formatCurrency(totalUsd)}</div>
              </div>
            </div>
            <div className="receipt-row receipt-header">
              <span>#</span>
              <span>Date</span>
              <span>Job</span>
              <span>Provider</span>
              <span>AEX</span>
              <span>USD</span>
            </div>
            <div className="receipt-list">
              {receipts.map((r) => (
                <div key={r.receipt_id} className="receipt-row">
                  <span>{r.sequence_number}</span>
                  <span>{formatDate(r.created_at)}</span>
                  <span className="mono">{truncateId(r.job_id)}</span>
                  <span className="mono">{truncateId(r.provider_aex_id ?? '—')}</span>
                  <span>{formatCurrency(r.amount_aex)}</span>
                  <span>${formatCurrency(r.amount_usd)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <main className="console-page">
      <header className="console-header">
        <h1>Stoa Console</h1>
        <button type="button" onClick={onLogout} className="link-button">
          Log out
        </button>
      </header>

      <section className="console-card" style={{ marginBottom: '1.5rem' }}>
        <dl className="console-meta">
          <dt>Display name</dt>
          <dd>{profile?.display_name ?? '—'}</dd>
          <dt>AEX ID</dt>
          <dd className="mono">{profile?.aex_id ?? '—'}</dd>
          <dt>Balance</dt>
          <dd>{profile ? `$${formatCurrency(profile.balance)}` : '—'}</dd>
        </dl>
        {profileError && <div className="auth-error" role="alert">{profileError}</div>}
        {/* Strictly null: a server that predates verification omits the field. */}
        {profile && profile.email_verified_at === null && (
          <div className="verify-notice" role="region" aria-label="Verify your email">
            <p>
              <strong>Verify your email.</strong> {profile.email} is not verified yet. A verified
              address is how you get back in if you lose your password.
            </p>
            <ResendVerification onVerified={refreshProfile} />
          </div>
        )}
      </section>

      <div className="console-tabs">
        {(['jobs', 'stalls', 'agents', 'balance', 'receipts', 'ops'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`console-tab${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'jobs' && renderJobs()}
      {activeTab === 'stalls' && renderStalls()}
      {activeTab === 'agents' && renderAgents()}
      {activeTab === 'balance' && renderBalance()}
      {activeTab === 'receipts' && renderReceipts()}
      {activeTab === 'ops' && renderOps()}
    </main>
  );
};

export default ConsolePlaceholder;
